import * as React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { isReleaseTypeHotfixOrRollback, statusColorForReleaseStatus } from '../helpers';
import {
    displayStringForRolloutPercentage,
    displayTextForReleaseType,
    statusStringForReleaseStatus,
} from '../helpers/displayHelpers';
import { RootState } from '../reducers/reducers';
import { AppPlatform, ReleaseStatus } from '../types';
import { ReleaseComponentProps } from '../typescriptHelpers';
import { antGrey, runwayColors, tagStyle } from './styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import OTATag from './OTATag';
import {
    selectRelease,
    selectReleaseDisplayableVersion,
    selectAppStoreType,
    selectAppPlatform,
} from '../selectors/selectors';
import { PlatformsLabel } from './sharedComponents/PlatformsLabel';
import {
    DestinationStatusDataSourceItem,
    DestinationStatusTagWithPopover,
} from './workflowOverview/widgets/WorkflowRunDestinationStatus';

export interface ReleaseVersionProps extends ReleaseComponentProps {
    statusDataSources: DestinationStatusDataSourceItem[];
}

const ReleaseVersion: React.FC<ReleaseVersionProps> = (props: ReleaseVersionProps) => {
    const { status, type } = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);

        return {
            status: release.status,
            type: release.type,
        };
    }, shallowEqual);
    const displayableVersion = useSelector((state: RootState) =>
        selectReleaseDisplayableVersion(state, props.releaseId),
    );
    const phasedReleasePercent = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);
        const phasedPercent =
            release.phasedRelease?.isManuallyAcceleratedPhasedRollout === true ?
                100
            :   release.phasedRelease?.phasedReleasePercent;

        return phasedPercent;
    });
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isCompleteButPhased = status === ReleaseStatus.Completed && !!phasedReleasePercent;
    const showStatusTag = status === ReleaseStatus.Completed || status === ReleaseStatus.Skipped;
    const showReleaseTypeTag = isReleaseTypeHotfixOrRollback(type);
    const showStatusTagModule = showStatusTag || showReleaseTypeTag;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                paddingBottom: 16,
                paddingLeft: 16,
                paddingRight: 16,
            }}
        >
            {appPlatform === AppPlatform.ReactNativeOTA && <OTATag />}
            <Space align="baseline" size={12} wrap style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', padding: '10px 0px' }}>
                    <PlatformsLabel appId={props.appId} platform={appPlatform} appStoreType={appStoreType} />
                </div>
                <Text style={{ fontSize: 30, textAlign: 'right', color: antGrey.grey8 }}>{displayableVersion}</Text>
            </Space>
            {props.statusDataSources.length > 0 ?
                <DestinationStatusTagWithPopover
                    destinationStatusDataSourceItems={props.statusDataSources}
                    shouldRenderStatusData
                />
            :   showStatusTagModule && (
                    <Space style={{ marginTop: 8, justifyContent: 'flex-end' }} size={8} wrap>
                        {showStatusTag && (
                            <Tag color={statusColorForReleaseStatus(status)} style={{ ...tagStyle, marginRight: 0 }}>
                                {statusStringForReleaseStatus(status)}
                            </Tag>
                        )}
                        {showReleaseTypeTag && (
                            <Tag color={antGrey.grey8} style={{ ...tagStyle, marginRight: 0 }}>
                                {displayTextForReleaseType(type)}
                            </Tag>
                        )}
                        {isCompleteButPhased && (
                            <Tag style={{ ...tagStyle, marginRight: 0, color: runwayColors.statusGreen }} color="white">
                                Phased {displayStringForRolloutPercentage(phasedReleasePercent)}
                            </Tag>
                        )}
                    </Space>
                )
            }
        </div>
    );
};

export default ReleaseVersion;
